import { Glass } from "@coach/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, Plus, Search, Sparkles } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { AppShell } from "../components/app-shell.js";
import { SplitButton } from "../components/split-button.jsx";
import { NoteCard } from "../features/notes/card.jsx";
import { useSync } from "../providers/sync-provider";
import type { BoardRow } from "../repos/boards.js";
import { createNote, listNotes, type NoteSummary, summarizeNote } from "../repos/notes.js";
import {
  createWhiteboard,
  listWhiteboards,
  summarizeWhiteboard,
  type WhiteboardSummary,
} from "../repos/whiteboards.js";

/**
 * Whiteboard — list route (`/whiteboard`).
 *
 * Mixed overview: shows both whiteboards and notes in one grid sorted by
 * `updatedAt` desc. The primary CTA is a split button — its main half
 * creates a whiteboard (default) and the dropdown offers "Neue Notiz".
 * Opening a card navigates to the appropriate editor route.
 */
export function WhiteboardPage() {
  const { db, orgId, userId, engine, status } = useSync();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const ready = Boolean(db && orgId && userId);

  const boards = useQuery<BoardRow[]>({
    queryKey: ["whiteboards", orgId, userId],
    enabled: ready,
    queryFn: () => listWhiteboards(db!, orgId!, userId!),
    refetchInterval: 5000,
  });
  const notes = useQuery<BoardRow[]>({
    queryKey: ["notes", orgId, userId],
    enabled: ready,
    queryFn: () => listNotes(db!, orgId!, userId!),
    refetchInterval: 5000,
  });

  const [search, setSearch] = useState("");

  const createBoard = useMutation({
    mutationFn: async () => {
      if (!ready) throw new Error("Lokale Datenbank nicht bereit.");
      const row = await createWhiteboard(db!, {
        orgId: orgId!,
        ownerKind: "coach",
        ownerId: userId!,
        name: defaultName(boards.data ?? [], "Whiteboard"),
      });
      void engine?.syncNow();
      return row;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ["whiteboards"], exact: false });
      navigate(`/whiteboard/${row.id}`);
    },
  });

  const createNoteMut = useMutation({
    mutationFn: async () => {
      if (!ready) throw new Error("Lokale Datenbank nicht bereit.");
      const row = await createNote(db!, {
        orgId: orgId!,
        ownerKind: "coach",
        ownerId: userId!,
        name: defaultName(notes.data ?? [], "Notiz"),
      });
      void engine?.syncNow();
      return row;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ["notes"], exact: false });
      navigate(`/whiteboard/note/${row.id}`);
    },
  });

  // Merge whiteboards + notes into a single, recency-sorted list.
  type Item = { kind: "whiteboard"; data: WhiteboardSummary } | { kind: "note"; data: NoteSummary };
  const boardItems: Item[] = (boards.data ?? []).map((row) => ({
    kind: "whiteboard",
    data: summarizeWhiteboard(row),
  }));
  const noteItems: Item[] = (notes.data ?? []).map((row) => ({
    kind: "note",
    data: summarizeNote(row),
  }));
  const merged = [...boardItems, ...noteItems].sort((a, b) =>
    b.data.updatedAt.localeCompare(a.data.updatedAt),
  );

  const q = search.trim().toLowerCase();
  const visible = q
    ? merged.filter((it) => {
        const hay = it.kind === "note" ? `${it.data.name} ${it.data.preview ?? ""}` : it.data.name;
        return hay.toLowerCase().includes(q);
      })
    : merged;

  const totalCount = merged.length;
  const busy = createBoard.isPending || createNoteMut.isPending;

  return (
    <AppShell title="Whiteboard">
      <div className="flex flex-col gap-5">
        <section className="glass-floating flex flex-wrap items-center gap-3 rounded-3xl px-6 py-5 sm:px-7">
          <div className="min-w-0 flex-1">
            <h1 className="home-display text-[20px] font-bold" style={{ color: "#f4f5f3" }}>
              Whiteboard
            </h1>
            <p className="text-[12px]" style={{ color: "var(--color-on-surface-variant)" }}>
              {ready
                ? `${totalCount} ${totalCount === 1 ? "Eintrag" : "Einträge"} gespeichert${status.kind === "idle" ? "" : ` · ${status.kind}`}`
                : "Lade lokale Datenbank…"}
            </p>
          </div>

          <label className="relative flex h-10 min-w-50 flex-1 items-center sm:max-w-70">
            <Search
              className="pointer-events-none absolute left-3 size-4 text-muted-foreground"
              aria-hidden
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Durchsuchen…"
              className="h-full w-full rounded-full border border-white/15 bg-white/4 pl-9 pr-3 text-body-sm outline-none focus:border-pitch-lime/60 focus:shadow-glow"
            />
          </label>

          <SplitButton
            primaryLabel={busy ? "Erstelle…" : "Neues Whiteboard"}
            primaryIcon={<Plus className="size-4" />}
            onPrimary={() => createBoard.mutate()}
            disabled={!ready || busy}
            primaryClassName="inline-flex h-10 items-center gap-2 rounded-l-full bg-pitch-lime pl-4 pr-3 text-body-sm font-semibold text-[#1d3700] transition-shadow hover:shadow-glow disabled:opacity-60"
            caretClassName="inline-flex h-10 items-center justify-center rounded-r-full border-l border-[#1d3700]/30 bg-pitch-lime px-2 text-[#1d3700] transition-shadow hover:shadow-glow disabled:opacity-60"
            items={[
              {
                id: "new-note",
                label: "Neue Notiz",
                description: "Text & Markdown",
                icon: <FileText className="size-4" />,
                onSelect: () => createNoteMut.mutate(),
              },
            ]}
          />
        </section>

        {visible.length === 0 ? (
          <Glass level="panel" padding="lg">
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <Sparkles className="size-8 text-pitch-lime" aria-hidden />
              <p className="text-display-sm font-semibold">
                {q ? "Keine Treffer" : "Noch nichts gespeichert"}
              </p>
              <p className="max-w-sm text-body-sm text-muted-foreground">
                {q
                  ? "Versuche es mit einem anderen Suchbegriff."
                  : "Lege ein neues Whiteboard an, um zu skizzieren — oder eine Notiz für Text & Markdown."}
              </p>
              {!q && (
                <button
                  type="button"
                  onClick={() => createBoard.mutate()}
                  disabled={!ready || busy}
                  className="mt-2 inline-flex h-10 items-center gap-2 rounded-full bg-pitch-lime px-4 text-body-sm font-semibold text-[#1d3700] hover:shadow-glow disabled:opacity-60"
                >
                  <Plus className="size-4" />
                  Erstes Whiteboard erstellen
                </button>
              )}
            </div>
          </Glass>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((it) =>
              it.kind === "whiteboard" ? (
                <WhiteboardCard
                  key={`w-${it.data.id}`}
                  summary={it.data}
                  onOpen={() => navigate(`/whiteboard/${it.data.id}`)}
                />
              ) : (
                <NoteCard
                  key={`n-${it.data.id}`}
                  summary={it.data}
                  onOpen={() => navigate(`/whiteboard/note/${it.data.id}`)}
                />
              ),
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function WhiteboardCard({ summary, onOpen }: { summary: WhiteboardSummary; onOpen: () => void }) {
  const dark = summary.bg === "dark";
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group text-left"
      aria-label={`Whiteboard ${summary.name} öffnen`}
    >
      <Glass
        level="floating"
        padding="md"
        className="flex flex-col gap-3 transition-shadow group-hover:shadow-glow"
      >
        {summary.cover ? (
          <div
            className="relative aspect-16/10 w-full overflow-hidden rounded-2xl"
            style={{ background: "#0e1417" }}
          >
            <img
              src={summary.cover}
              alt=""
              className="absolute inset-0 h-full w-full object-contain"
              loading="lazy"
              decoding="async"
            />
          </div>
        ) : (
          <div
            className="relative aspect-16/10 w-full overflow-hidden rounded-2xl"
            style={{
              background: dark ? "#101418" : "#f5f5f0",
              backgroundImage: dark
                ? "radial-gradient(rgba(255,255,255,0.18) 1px, transparent 1px)"
                : "radial-gradient(rgba(0,0,0,0.18) 1px, transparent 1px)",
              backgroundSize: "20px 20px",
              backgroundPosition: "0 0",
            }}
          />
        )}
        <div className="flex flex-col gap-1">
          <span className="text-body font-semibold leading-tight">{summary.name}</span>
          <span className="text-[11px] uppercase tracking-widest text-muted-foreground">
            {dark ? "Dunkel" : "Hell"} · {formatDate(summary.updatedAt)}
          </span>
        </div>
      </Glass>
    </button>
  );
}

function defaultName(existing: BoardRow[], base: string): string {
  const taken = new Set(existing.map((b) => b.name));
  let n = existing.length + 1;
  while (taken.has(`${base} ${n}`)) n++;
  return `${base} ${n}`;
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return "";
  }
}
